import { useApolloClient } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import parsePhoneNumberFromString, { type CountryCode } from 'libphonenumber-js'
import { useCallback, useEffect, useMemo } from 'react'
import { type Resolver, useForm } from 'react-hook-form'
import { z } from 'zod'
import { TermsDialog } from '../components/TermsDialog'
import { Countries, countryByISO } from '../data/countries'
import {
  ConsumerCarriersDocument,
  useConsumerDetailsQuery,
  useConsumerUpdateMutation,
} from '../generated/graphql'
import { useTranslation } from '../localization/useTranslation'
import { useAuthStore } from '../store/auth'
import { useCheckoutStore } from '../store/checkout'
import { useSetPageTitle } from '../store/pageTitle'
import { syncConsumerCurrency } from '../util/syncConsumerCurrency'

/**
 * Details step: the customer's own contact + address — the *sender* on the prepaid label we print
 * to route their parcel to our warehouse (hence "Your details", not "where we send it"). Wired with
 * react-hook-form + a zod schema (v1 parity): prefilled from `ConsumerById`, country-aware postal
 * validation, and saved via `ConsumerUpdate` when the customer continues (which refetches
 * `ConsumerCarriers`, since the shipping options depend on the saved country/address). Form validity
 * and terms acceptance are pushed to the checkout store so FlowLayout gates Continue on both.
 */
interface FormValues {
  name: string
  mail: string
  countryCode: string
  address: string
  zipCode: string
  city: string
  phonePrefix: string
  phone: string
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^[\d ]+$/

/** Parse an existing `+45 12 34 56 78` phone into (prefix, national number); tolerant of junk. */
function splitPhone(phone: string | null | undefined, iso: CountryCode) {
  if (!phone) return { prefix: '', national: '' }
  try {
    const parsed = parsePhoneNumberFromString(phone, iso)
    if (parsed) return { prefix: parsed.countryCallingCode, national: parsed.nationalNumber }
  } catch {
    /* fall through to empty */
  }
  return { prefix: '', national: '' }
}

export function DetailsPage() {
  const client = useApolloClient()
  const accepted = useCheckoutStore((s) => s.termsAccepted)
  const setAccepted = useCheckoutStore((s) => s.setTermsAccepted)
  const setDetailsValid = useCheckoutStore((s) => s.setDetailsValid)
  const setOnBeforeNext = useCheckoutStore((s) => s.setOnBeforeNext)
  const consumerId = useAuthStore((s) => s.consumerId)
  const shopId = useAuthStore((s) => s.shopId)
  // When the shop locks email (email is the key for an external-system integration), it's read-only:
  // the value stays and is still required, so an empty locked email blocks Continue by design.
  const lockEmail = useAuthStore((s) => s.config.lockEmail) ?? false
  const { t } = useTranslation()
  useSetPageTitle(t('details.title'), t('details.subtitle'))

  // Accepting inside the terms dialog just ticks the consent box. (We used to scroll it into view
  // afterwards, to undo a background scroll the terms dialog caused — but the dialog no longer
  // scrolls the page, so that scroll now just yanks the page down unexpectedly.)
  const acceptTerms = () => setAccepted(true)

  // Country-aware schema: the postal-code rule depends on the currently-selected country.
  const schema = useMemo(
    () =>
      z
        .object({
          name: z.string().trim().min(1, t('details.required')),
          mail: z
            .string()
            .trim()
            .min(1, t('details.required'))
            .regex(EMAIL_RE, t('details.emailInvalid')),
          countryCode: z.string().min(1, t('details.required')),
          address: z.string().trim().min(1, t('details.required')),
          zipCode: z.string().trim().min(1, t('details.required')),
          city: z.string().trim().min(1, t('details.required')),
          phonePrefix: z.string().min(1, t('details.required')),
          phone: z
            .string()
            .trim()
            .min(1, t('details.required'))
            .regex(PHONE_RE, t('details.phoneInvalid')),
        })
        .superRefine((val, ctx) => {
          if (!val.zipCode) return
          const country = countryByISO(val.countryCode)
          const re = country?.Regex ? new RegExp(country.Regex) : /^\d{4,5}$/
          if (!re.test(val.zipCode)) {
            const hint = country?.Format
              ? ` (${t('details.zipFormat', { format: country.Format })})`
              : ''
            ctx.addIssue({ code: 'custom', path: ['zipCode'], message: t('details.zipInvalid') + hint })
          }
        }),
    [t],
  )

  const {
    register,
    reset,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema) as Resolver<FormValues>,
    defaultValues: {
      name: '',
      mail: '',
      countryCode: 'DK',
      address: '',
      zipCode: '',
      city: '',
      phonePrefix: '45',
      phone: '',
    },
  })

  const { data, loading } = useConsumerDetailsQuery({
    variables: { consumerId: consumerId ?? '' },
    skip: !consumerId,
    fetchPolicy: 'cache-and-network',
  })

  // Prefill from the saved consumer once it resolves.
  useEffect(() => {
    const c = data?.ConsumerById
    if (!c) return
    const iso = (c.countryCode as CountryCode) || 'DK'
    const { prefix, national } = splitPhone(c.phone, iso)
    reset({
      name: c.name ?? '',
      mail: c.mail ?? '',
      countryCode: c.countryCode ?? 'DK',
      address: c.add1 ?? '',
      zipCode: c.zipCode ?? '',
      city: c.city ?? '',
      phonePrefix: prefix || countryByISO(iso)?.PhonePrefix || '45',
      phone: national,
    })
  }, [data, reset])

  // Terms must be an explicit, fresh choice every time the form is shown — never pre-ticked.
  useEffect(() => {
    setAccepted(false)
  }, [setAccepted])

  // Surface form validity to FlowLayout so Continue is gated on it (with terms).
  useEffect(() => {
    setDetailsValid(isValid)
  }, [isValid, setDetailsValid])

  const [saveConsumer] = useConsumerUpdateMutation({
    refetchQueries:
      shopId && consumerId
        ? [{ query: ConsumerCarriersDocument, variables: { shopId, consumerId } }]
        : [],
    awaitRefetchQueries: true,
  })

  // Registered with the store so pressing Continue validates + saves the address before advancing;
  // saving refetches the carriers the Shipping step needs.
  const submit = useCallback(async () => {
    if (!consumerId) return false
    if (!(await trigger())) return false
    const v = getValues()
    try {
      const { data: saved } = await saveConsumer({
        variables: {
          consumer: {
            _id: consumerId,
            shopId: shopId ?? undefined,
            name: v.name,
            mail: v.mail.toLowerCase(),
            countryCode: v.countryCode,
            add1: v.address,
            zipCode: v.zipCode,
            city: v.city,
            phone: `+${v.phonePrefix} ${v.phone}`,
          },
        },
      })
      syncConsumerCurrency(client.cache, saved?.ConsumerUpdate)
      return Boolean(saved?.ConsumerUpdate?._id)
    } catch {
      return false
    }
  }, [consumerId, shopId, trigger, getValues, saveConsumer, client])

  useEffect(() => {
    setOnBeforeNext(submit)
    return () => setOnBeforeNext(null)
  }, [submit, setOnBeforeNext])

  if (loading && !data) {
    return (
      <div className="center-note">
        <p>{t('details.loading')}</p>
      </div>
    )
  }

  return (
    <div className="step">
      <form className="form" onSubmit={(e) => e.preventDefault()}>
        <div className="fieldset-title">
          <h2>{t('details.contact')}</h2>
        </div>
        <div className="fgroup">
          <div className="frow">
            <div className="field">
              <label htmlFor="f-name">{t('details.fullName')}</label>
              {/* autoComplete tokens are what unlock iOS Safari's Contact AutoFill (desktop guesses
                  from labels/names, iOS needs the standard tokens). */}
              <input id="f-name" autoComplete="name" aria-invalid={!!errors.name} {...register('name')} />
              {errors.name && <div className="err">{errors.name.message}</div>}
            </div>
            <div className="field">
              <label htmlFor="f-email">{t('details.email')}</label>
              <input
                id="f-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                aria-invalid={!!errors.mail}
                readOnly={lockEmail}
                aria-readonly={lockEmail || undefined}
                {...register('mail')}
              />
              {errors.mail && <div className="err">{errors.mail.message}</div>}
              {lockEmail && <div className="hint">{t('details.emailLocked')}</div>}
            </div>
          </div>
        </div>

        <div className="divider" />

        <div className="fieldset-title">
          <h2>{t('details.address')}</h2>
        </div>
        <div className="fgroup">
          <div className="field">
            <label htmlFor="f-country">{t('details.country')}</label>
            <select
              id="f-country"
              autoComplete="country"
              aria-invalid={!!errors.countryCode}
              {...register('countryCode')}
            >
              {Countries.map((c) => (
                <option key={c.ISO} value={c.ISO}>
                  {c.Country}
                </option>
              ))}
            </select>
            {errors.countryCode && <div className="err">{errors.countryCode.message}</div>}
          </div>
          <div className="field">
            <label htmlFor="f-address">{t('details.streetAddress')}</label>
            <input
              id="f-address"
              autoComplete="street-address"
              aria-invalid={!!errors.address}
              {...register('address')}
            />
            {errors.address && <div className="err">{errors.address.message}</div>}
          </div>
          <div className="frow z">
            <div className="field">
              <label htmlFor="f-zip">{t('details.postalCode')}</label>
              <input
                id="f-zip"
                autoComplete="postal-code"
                aria-invalid={!!errors.zipCode}
                {...register('zipCode')}
              />
              {errors.zipCode && <div className="err">{errors.zipCode.message}</div>}
            </div>
            <div className="field">
              <label htmlFor="f-city">{t('details.city')}</label>
              <input
                id="f-city"
                autoComplete="address-level2"
                aria-invalid={!!errors.city}
                {...register('city')}
              />
              {errors.city && <div className="err">{errors.city.message}</div>}
            </div>
          </div>
          <div className="frow p">
            <div className="field">
              <label htmlFor="f-prefix">{t('details.prefix')}</label>
              <select
                id="f-prefix"
                autoComplete="tel-country-code"
                aria-invalid={!!errors.phonePrefix}
                {...register('phonePrefix')}
              >
                {Countries.map((c) => (
                  <option key={c.ISO} value={c.PhonePrefix}>
                    {c.Country} +{c.PhonePrefix}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="f-phone">{t('details.phone')}</label>
              <input
                id="f-phone"
                type="tel"
                autoComplete="tel-national"
                inputMode="tel"
                aria-invalid={!!errors.phone}
                {...register('phone')}
              />
              {errors.phone && <div className="err">{errors.phone.message}</div>}
            </div>
          </div>
        </div>

        <div className="consent">
          <input
            type="checkbox"
            id="terms-cb"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <div>
            <p>
              {t('details.consentPre')}
              <TermsDialog
                trigger={
                  <button type="button" className="link">
                    {t('details.consentLink')}
                  </button>
                }
                onAccept={acceptTerms}
              />
              {t('details.consentPost')}
            </p>
          </div>
        </div>
      </form>
    </div>
  )
}
